"use client";

import { AuthCardIcon } from "@/components/auth/auth-card-icon";
import { LoginForm } from "@/components/auth/login-form";
import Link from "@/components/localized-link";
import { Button } from "@/components/shadcn/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/shadcn/dialog";
import { UserRound } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

interface LoginModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

export function LoginModal({ open, onOpenChange, onSuccess }: LoginModalProps) {
    const t = useTranslations("auth");
    const router = useRouter();

    const handleSuccess = () => {
        onOpenChange(false);
        onSuccess?.();
    };

    const handleEmailNotVerified = (username: string) => {
        onOpenChange(false);
        router.push(
            `/send-new-registration-link?username=${encodeURIComponent(username)}`,
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md">
                <DialogHeader className="items-center text-center sm:text-center">
                    <AuthCardIcon icon={UserRound} />
                    <DialogTitle>{t("loginTitle")}</DialogTitle>
                    <DialogDescription>
                        {t("loginDescription")}
                    </DialogDescription>
                </DialogHeader>

                <LoginForm
                    onSuccess={handleSuccess}
                    onEmailNotVerified={handleEmailNotVerified}
                />

                <p className="text-muted-foreground flex flex-wrap items-center justify-center gap-1 text-center text-sm">
                    <span>{t("noAccount")}</span>
                    <Button
                        asChild
                        type="button"
                        variant="link"
                        className="text-foreground h-auto p-0"
                    >
                        <Link href="/register">{t("register")}</Link>
                    </Button>
                </p>
            </DialogContent>
        </Dialog>
    );
}
